// Snapshot merge for the sync layer. Per the design:
//   - library, mastered, customPersonas, viewedStories are *additive* — union
//     from both sides, never lose data.
//   - chatStates, relationships, profile are *newest-wins* based on the
//     snapshot's `savedAt` field.
//   - skipAuth, savedAt itself follow newest-wins (savedAt = max).
//
// The function also reports `divergence`: the set of newest-wins fields where
// both sides have meaningful content AND they differ. The UI consumes this to
// decide whether to show the conflict prompt; it's empty if either side is
// trivially empty or if both sides agree.

import type { AppSnapshot } from './repository';
import type { ChatState, LibraryWord, Persona, Relationship } from '../types';

export type DivergenceField = 'chatStates' | 'relationships' | 'profile';

export interface MergeResult {
  merged: AppSnapshot;
  /** Fields where both sides had non-trivial data and the values differ. */
  divergence: DivergenceField[];
  /** Which side was preferred for newest-wins fields. */
  newer: 'local' | 'server' | 'equal';
  /** Original snapshots — kept so the conflict prompt can re-apply a chosen side. */
  local: AppSnapshot;
  server: AppSnapshot;
}

/**
 * Override the merged snapshot's newest-wins fields with one specific side.
 * Additive collections (library, mastered, customPersonas, viewedStories) stay
 * as merged — they're never lost.
 */
export function applyResolution(
  result: MergeResult,
  side: 'local' | 'server',
): AppSnapshot {
  const pick = side === 'local' ? result.local : result.server;
  return {
    ...result.merged,
    chatStates: pick.chatStates,
    relationships: pick.relationships,
    profile: pick.profile,
    savedAt: new Date().toISOString(),
  };
}

function unionByKey<T>(local: T[], server: T[], key: (t: T) => string): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const item of local) {
    const k = key(item);
    if (!seen.has(k)) {
      seen.add(k);
      out.push(item);
    }
  }
  for (const item of server) {
    const k = key(item);
    if (!seen.has(k)) {
      seen.add(k);
      out.push(item);
    }
  }
  return out;
}

function pickNewer<T>(local: T, server: T, localTs: string, serverTs: string): T {
  return Date.parse(serverTs) > Date.parse(localTs) ? server : local;
}

function isMeaningfulChat(map: Record<string, ChatState> | undefined): boolean {
  if (!map) return false;
  for (const id in map) {
    const cs = map[id];
    // A seeded greeting is one message; consider only longer histories.
    if (cs && cs.history.length > 1) return true;
  }
  return false;
}

function isMeaningfulRelationships(map: Record<string, Relationship> | undefined): boolean {
  if (!map) return false;
  for (const id in map) {
    const r = map[id];
    if (r?.matched || r?.passed || (r?.warmth ?? 0) > 0) return true;
  }
  return false;
}

function chatStatesDiffer(a: Record<string, ChatState>, b: Record<string, ChatState>): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return true;
  for (const k of aKeys) {
    const ah = a[k]?.history.length ?? 0;
    const bh = b[k]?.history.length ?? 0;
    if (ah !== bh) return true;
    // Last message timestamp comparison — cheap signal of divergent histories.
    const aLast = a[k]?.history.at(-1)?.ts ?? 0;
    const bLast = b[k]?.history.at(-1)?.ts ?? 0;
    if (aLast !== bLast) return true;
  }
  return false;
}

function relationshipsDiffer(a: Record<string, Relationship>, b: Record<string, Relationship>): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) {
    const ar = a[k];
    const br = b[k];
    if (!ar || !br) return true;
    if (ar.matched !== br.matched) return true;
    if ((ar.passed ?? false) !== (br.passed ?? false)) return true;
  }
  return false;
}

export function mergeSnapshots(local: AppSnapshot, server: AppSnapshot): MergeResult {
  const localTs = local.savedAt || new Date(0).toISOString();
  const serverTs = server.savedAt || new Date(0).toISOString();
  const newer: 'local' | 'server' | 'equal' =
    Date.parse(serverTs) > Date.parse(localTs)
      ? 'server'
      : Date.parse(localTs) > Date.parse(serverTs)
        ? 'local'
        : 'equal';

  // Additive collections — pure unions, no divergence ever.
  const library = unionByKey<LibraryWord>(local.library, server.library, (w) => w.hz);
  const mastered = unionByKey<LibraryWord>(local.mastered, server.mastered, (w) => w.hz);
  const customPersonas = unionByKey<Persona>(local.customPersonas, server.customPersonas, (p) => p.id);
  const viewedStories = { ...local.viewedStories, ...server.viewedStories };

  // Newest-wins fields.
  const chatStates = pickNewer(local.chatStates, server.chatStates, localTs, serverTs);
  const relationships = pickNewer(local.relationships, server.relationships, localTs, serverTs);
  const profile = pickNewer(local.profile, server.profile, localTs, serverTs);
  const skipAuth = newer === 'server' ? server.skipAuth : local.skipAuth;
  // nudgeDismissed: once dismissed on either device, stay dismissed everywhere.
  const nudgeDismissed = local.nudgeDismissed || server.nudgeDismissed;
  // hasSeenWelcome / skippedSetup: sticky-true like nudgeDismissed — once
  // either device has cleared the pre-auth welcome or skipped the post-auth
  // setup, don't reshow it on the other.
  const hasSeenWelcome = local.hasSeenWelcome || server.hasSeenWelcome;
  const skippedSetup = local.skippedSetup || server.skippedSetup;
  // aiProvider: newest-wins (kept in storage for back-compat though the
  // Settings toggle is gone).
  const aiProvider = newer === 'server' ? server.aiProvider : local.aiProvider;
  // favorites: newest-wins on the whole map. Union would let an unstar on
  // device A be undone by a stale star still present on device B.
  const favorites = newer === 'server' ? server.favorites : local.favorites;
  // personaMemories: per-persona pickNewer reuses the chatStates pattern.
  // Counter and facts move together with whichever device chatted last.
  const personaMemories = pickNewer(local.personaMemories, server.personaMemories, localTs, serverTs);

  // Divergence — only flag fields where BOTH sides had meaningful data AND they actually differ.
  const divergence: DivergenceField[] = [];
  if (
    isMeaningfulChat(local.chatStates) &&
    isMeaningfulChat(server.chatStates) &&
    chatStatesDiffer(local.chatStates, server.chatStates)
  ) {
    divergence.push('chatStates');
  }
  if (
    isMeaningfulRelationships(local.relationships) &&
    isMeaningfulRelationships(server.relationships) &&
    relationshipsDiffer(local.relationships, server.relationships)
  ) {
    divergence.push('relationships');
  }
  if (
    local.profile.streak > 0 &&
    server.profile.streak > 0 &&
    (local.profile.streak !== server.profile.streak ||
      local.profile.lastActiveDay !== server.profile.lastActiveDay)
  ) {
    divergence.push('profile');
  }

  const merged: AppSnapshot = {
    version: Math.max(local.version, server.version),
    profile,
    aiProvider,
    skipAuth,
    nudgeDismissed,
    hasSeenWelcome,
    skippedSetup,
    savedAt: localTs > serverTs ? localTs : serverTs,
    library,
    mastered,
    relationships,
    chatStates,
    viewedStories,
    favorites,
    personaMemories,
    customPersonas,
  };

  return { merged, divergence, newer, local, server };
}
